/**
 * The Inventar article list — Inventar 2.0, slice S5.
 *
 * Extracted from AdminInventoryPage so the row can be reworked and tested on
 * its own; the page keeps the shell, KPI section and filter bar that S4 built
 * and now only says "render these rows".
 *
 * ONE LAYOUT AT A TIME, NOT TWO HIDDEN BY CSS. Desktop and mobile are chosen
 * in JavaScript via useIsMobile, not with `hidden md:block` / `md:hidden`.
 * With the CSS approach both markups sit in the DOM and the browser fetches
 * BOTH thumbnails — every article costs two image requests, one of which is
 * never seen. Picking in JS means exactly one <img> per article, which is the
 * point of S3 putting primary_thumb_url in the list response at all. The
 * breakpoint is the repo's locked 768px (hooks/useIsMobile.js).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ImageOff } from "lucide-react";

import { useIsMobile } from "../../../hooks/useIsMobile";
import { inventoryStatusLabelDe } from "../../../utils/inventoryExport";
import { inventoryStatusToneClass } from "../../../utils/inventoryStatus";

/** CHF with the Swiss thousands separator, matching the rest of the page. */
function formatChf(value) {
  if (value == null || value === "") return "—";
  return `CHF ${Number(value).toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function StatusBadge({ status }) {
  const label = inventoryStatusLabelDe(status);
  if (!label) return <span className="text-muted-foreground">—</span>;
  /* Tone moved to utils/inventoryStatus in S6b so the detail page reads the
     same map instead of carrying a third one. Behaviour is unchanged. */
  const tone = inventoryStatusToneClass(status);
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2 py-[2px] text-[10px] font-semibold ${tone}`}
    >
      {label}
    </span>
  );
}

function CategoryBadge({ category }) {
  if (!category) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex whitespace-nowrap rounded-full border border-border bg-muted px-2 py-[2px] text-[10px] font-medium text-muted-foreground">
      {category}
    </span>
  );
}

/**
 * The article's own photo, or a neutral placeholder in the same box.
 *
 * Fixed box in both cases, so a row never changes height when an image
 * arrives or fails. alt="" because the article name sits right beside it --
 * announcing the filename again would just be noise for a screen reader.
 *
 * A broken URL falls back to the placeholder instead of the browser's broken
 * image glyph. That is one boolean of state, and it is worth it: these URLs
 * are presigned and expire, so a stale page WILL eventually render some of
 * them broken.
 *
 * Never product_url: that is a dealer's web page, not our photo.
 */
function Thumbnail({ url, size = "h-9 w-9" }) {
  const [failed, setFailed] = useState(false);
  const box = `${size} shrink-0 overflow-hidden rounded-[8px] border border-border bg-muted`;

  if (!url || failed) {
    return (
      <div className={`${box} flex items-center justify-center`} aria-hidden>
        <ImageOff className="h-4 w-4 text-muted-foreground/60" />
      </div>
    );
  }
  return (
    <div className={box}>
      <img
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function ProductLink({ url, compact = false }) {
  const href = String(url || "").trim();
  if (!href) return compact ? null : <span className="text-muted-foreground">—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 rounded-[6px] border border-border bg-muted px-2 py-[3px] text-[10.5px] text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ExternalLink className="h-3 w-3" aria-hidden />
      Produkt
    </a>
  );
}

/**
 * One stock figure as a pill.
 *
 * S7c: total, assigned and free used to share one cell as a small paragraph.
 * The reference gives each its own numeric column with a coloured badge, which
 * is easier to scan down and is what a table is for.
 *
 * Every number is still the backend's own — total_quantity, assigned_total and
 * available, printed exactly as they arrive, no client arithmetic and no
 * correction of the clamp the service applies. Colour never carries meaning on
 * its own: the column header names the figure.
 */
const STOCK_PILL =
  "inline-flex min-w-[30px] items-center justify-center rounded-full px-2 py-0.5 font-mono text-[11.5px] font-semibold tabular-nums";

function StockPill({ value, tone }) {
  const cls =
    tone === "assigned" && value > 0
      ? "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400"
      : tone === "free" && value > 0
        ? "bg-amber-500/12 text-amber-700 dark:text-amber-400"
        : tone === "total"
          ? "bg-foreground/85 text-background"
          : "bg-muted text-muted-foreground";
  return <span className={`${STOCK_PILL} ${cls}`}>{value}</span>;
}

/** Name, number, brand — enough to recognise the article, not its whole record. */
function IdentityText({ row, onOpen }) {
  return (
    <div className="min-w-0">
      <Link
        to={`/admin/inventory/${row.id}`}
        onClick={(e) => {
          e.stopPropagation();
          if (onOpen) onOpen();
        }}
        className="block truncate text-[12.5px] font-semibold text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={row.name}
      >
        {row.name}
      </Link>
      <p className="m-0 mt-0.5 truncate text-[10.5px] text-muted-foreground">
        <span className="font-mono">{row.inventory_number}</span>
        {row.brand ? <span> · {row.brand}</span> : null}
      </p>
    </div>
  );
}

const TH = "whitespace-nowrap px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground";

function DesktopTable({ items, showRoomQtyCol, roomQtyLabel, pageTotalChf, onOpenItem }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className={TH}>Artikel</th>
            <th scope="col" className={TH}>Kategorie</th>
            <th scope="col" className={TH}>Lief.-Nr.</th>
            <th scope="col" className={TH}>Bezug</th>
            <th scope="col" className={`${TH} text-center`}>Gesamt</th>
            <th scope="col" className={`${TH} text-center`}>Zugeordnet</th>
            <th scope="col" className={`${TH} text-center`}>Frei</th>
            {showRoomQtyCol ? <th scope="col" className={`${TH} text-right`}>{roomQtyLabel}</th> : null}
            <th scope="col" className={TH}>Produkt</th>
            <th scope="col" className={`${TH} text-right`}>Anschaffung</th>
            <th scope="col" className={TH}>
              <span className="sr-only">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row.id}
              className="cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-muted/60"
              onClick={() => onOpenItem(row.id)}
            >
              <td className="px-3 py-2 align-middle">
                <div className="flex min-w-0 items-center gap-2.5">
                  <Thumbnail url={row.primary_thumb_url} />
                  <IdentityText row={row} />
                </div>
              </td>
              <td className="px-3 py-2 align-middle">
                {/* Status joins the category cell rather than claiming a column
                    of its own: the reference has no status column, and we are
                    not dropping the value. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <CategoryBadge category={row.category} />
                  <StatusBadge status={row.status} />
                </div>
              </td>
              <td className="max-w-[130px] px-3 py-2 align-middle">
                <p
                  className="m-0 truncate font-mono text-[10.5px] text-muted-foreground"
                  title={row.supplier_article_number || ""}
                >
                  {row.supplier_article_number || "—"}
                </p>
              </td>
              <td className="max-w-[130px] px-3 py-2 align-middle">
                <p
                  className="m-0 truncate text-[11px] text-muted-foreground"
                  title={row.purchased_from || ""}
                >
                  {row.purchased_from || "—"}
                </p>
              </td>
              <td className="px-3 py-2 text-center align-middle">
                <StockPill value={row.total_quantity ?? 1} tone="total" />
              </td>
              <td className="px-3 py-2 text-center align-middle">
                <StockPill value={row.assigned_total ?? 0} tone="assigned" />
              </td>
              <td className="px-3 py-2 text-center align-middle">
                <StockPill value={row.available ?? 0} tone="free" />
              </td>
              {showRoomQtyCol ? (
                <td className="px-3 py-2 text-right align-middle font-mono text-[12px] font-medium text-primary">
                  {Number(row.room_quantity || 0)}
                </td>
              ) : null}
              {/* S8: the product link gets the column the reference gives it.
                  Squeezed into the actions cell it was invisible when absent —
                  the row simply showed "Öffnen →" and you could not tell
                  whether the article had a product URL at all. Non-compact
                  here, so a missing URL reads as an em dash. */}
              <td className="px-3 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                <ProductLink url={row.product_url} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right align-middle font-mono text-[12px] font-medium text-foreground">
                {formatChf(row.purchase_price_chf)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right align-middle">
                <div
                  className="flex items-center justify-end gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => onOpenItem(row.id)}
                    aria-label={`${row.name} öffnen`}
                    className="rounded-[6px] border border-border bg-muted px-2.5 py-1 text-[10.5px] text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Öffnen →
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-border bg-muted">
            {/* Everything left of Anschaffung: Artikel, Kategorie, Lief.-Nr.,
                Bezug, Gesamt, Zugeordnet, Frei, Produkt (+ Menge im Raum). */}
            <td colSpan={showRoomQtyCol ? 9 : 8} className="px-3 py-2 text-[11px] text-muted-foreground">
              Summe dieser Seite
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[12px] font-bold text-foreground">
              {formatChf(pageTotalChf)}
            </td>
            <td className="px-3 py-2" aria-hidden />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function MobileList({ items, showRoomQtyCol, roomQtyLabel, pageTotalChf, onOpenItem }) {
  return (
    <div className="flex flex-col">
      {items.map((row) => (
        <article
          key={row.id}
          className="flex flex-col gap-2 border-b border-border p-3 last:border-b-0"
        >
          <div className="flex min-w-0 items-start gap-3">
            <Thumbnail url={row.primary_thumb_url} size="h-12 w-12" />
            <div className="min-w-0 flex-1">
              <IdentityText row={row} onOpen={() => onOpenItem(row.id)} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <CategoryBadge category={row.category} />
            <StatusBadge status={row.status} />
          </div>
          <div className="flex items-end justify-between gap-3 border-t border-border pt-2">
            {/* The same three pills as the desktop columns, labelled inline
                because a phone card has no column headers to lean on. */}
            <dl className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1">
              {[
                { label: "Gesamt", value: row.total_quantity ?? 1, tone: "total" },
                { label: "Zugeordnet", value: row.assigned_total ?? 0, tone: "assigned" },
                { label: "Frei", value: row.available ?? 0, tone: "free" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-1.5">
                  <dt className="m-0 text-[10px] text-muted-foreground">{c.label}</dt>
                  <dd className="m-0">
                    <StockPill value={c.value} tone={c.tone} />
                  </dd>
                </div>
              ))}
            </dl>
            <div className="text-right">
              {showRoomQtyCol ? (
                <p className="m-0 text-[10.5px] text-muted-foreground">
                  {roomQtyLabel}:{" "}
                  <span className="font-mono font-medium text-primary">
                    {Number(row.room_quantity || 0)}
                  </span>
                </p>
              ) : null}
              <p className="m-0 font-mono text-[12px] font-medium text-foreground">
                {formatChf(row.purchase_price_chf)}
              </p>
            </div>
          </div>
          {row.supplier_article_number || row.purchased_from ? (
            <p className="m-0 truncate text-[10.5px] text-muted-foreground">
              {[row.supplier_article_number, row.purchased_from].filter(Boolean).join(" · ")}
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <ProductLink url={row.product_url} compact />
            <button
              type="button"
              onClick={() => onOpenItem(row.id)}
              aria-label={`${row.name} öffnen`}
              className="ml-auto rounded-[8px] border border-border bg-muted px-3 py-1.5 text-[11.5px] font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Öffnen →
            </button>
          </div>
        </article>
      ))}
      <div className="flex items-center justify-between gap-3 border-t border-border bg-muted px-3 py-2.5">
        <span className="text-[11px] text-muted-foreground">Summe dieser Seite</span>
        <span className="font-mono text-[12px] font-bold text-foreground">
          {formatChf(pageTotalChf)}
        </span>
      </div>
    </div>
  );
}

export default function InventoryList(props) {
  const isMobile = useIsMobile();
  return isMobile ? <MobileList {...props} /> : <DesktopTable {...props} />;
}

export { Thumbnail, StatusBadge, StockPill };
